import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Section, SectionHeader } from "./_helpers";
import { Users, UserPlus, UserMinus, Check, X, Star, FolderPlus, Trash2 } from "lucide-react";

export default function VennerPage() {
  const { t } = useTranslation();
  const [friends, setFriends] = useState([]);
  const [pending, setPending] = useState({ incoming: [], outgoing: [] });
  const [closeFriends, setCloseFriends] = useState([]);
  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupMembers, setNewGroupMembers] = useState("");
  const [busy, setBusy] = useState({});

  const load = async () => {
    const [f, p, cf, gs] = await Promise.all([
      api.get("/friendships/me/list").then(r=>r.data).catch(()=>[]),
      api.get("/friendships/me/pending").then(r=>r.data).catch(()=>({incoming:[], outgoing:[]})),
      api.get("/friendships/me/close-friends").then(r=>r.data).catch(()=>[]),
      api.get("/friendships/me/groups").then(r=>r.data).catch(()=>[]),
    ]);
    setFriends(f);
    setPending(p);
    setCloseFriends(cf);
    setGroups(gs);
  };

  useEffect(() => { load(); }, []);

  const accept = async (fid) => {
    setBusy({...busy, [fid]: true});
    try { await api.post(`/friendships/${fid}/accept`); toast.success(t("venner.request_accepted")); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy({...busy, [fid]: false}); }
  };
  const reject = async (fid) => {
    setBusy({...busy, [fid]: true});
    try { await api.post(`/friendships/${fid}/reject`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy({...busy, [fid]: false}); }
  };
  const remove = async (username) => {
    if (!window.confirm(t("venner.remove_friend_confirm", { user: username }))) return;
    try { await api.delete(`/friendships/${username}`); toast.success(t("venner.removed_toast")); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleCloseFriend = async (username) => {
    const isClose = closeFriends.find(c => c.username === username);
    const newList = isClose
      ? closeFriends.filter(c => c.username !== username).map(c => c.username)
      : [...closeFriends.map(c => c.username), username];
    try {
      await api.put("/friendships/me/close-friends", { usernames: newList });
      toast.success(isClose ? t("venner.close_removed", { user: username }) : t("venner.close_added", { user: username }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createGroup = async () => {
    if (newGroupName.trim().length < 2) return toast.error(t("venner.group_name_too_short"));
    const members = newGroupMembers.split(/[,\s]+/).map(u => u.trim()).filter(Boolean);
    try {
      await api.post("/friendships/me/groups", { name: newGroupName.trim(), usernames: members });
      toast.success(t("venner.group_created", { name: newGroupName }));
      setNewGroupName("");
      setNewGroupMembers("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteGroup = async (gid, name) => {
    if (!window.confirm(t("venner.delete_group_confirm", { name }))) return;
    try { await api.delete(`/friendships/me/groups/${gid}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <Section testId="incoming-requests-section">
        <SectionHeader icon={UserPlus} title={t("venner.incoming_title")} />
        {pending.incoming.length === 0 ? (
          <p className="font-fraunces italic text-[var(--cream-soft)]">{t("venner.no_incoming")}</p>
        ) : (
          <ul className="border-y border-white/10 divide-y divide-white/5" data-testid="incoming-list">
            {pending.incoming.map((r) => (
              <li key={r.friendship_id} className="py-3 flex items-center justify-between">
                <Link to={`/profile/${r.user?.username}`} className="font-fraunces italic text-lg text-[var(--cream)] hover:text-[var(--magenta)]">
                  @{r.user?.username}
                </Link>
                <div className="flex gap-2">
                  <button onClick={()=>accept(r.friendship_id)} disabled={busy[r.friendship_id]}
                    className="btn-magenta inline-flex items-center gap-1" data-testid={`accept-${r.friendship_id}`}>
                    <Check className="h-3.5 w-3.5" /> {t("venner.accept")}
                  </button>
                  <button onClick={()=>reject(r.friendship_id)} disabled={busy[r.friendship_id]}
                    className="btn-ghost inline-flex items-center gap-1" data-testid={`reject-${r.friendship_id}`}>
                    <X className="h-3.5 w-3.5" /> {t("venner.reject")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section testId="outgoing-requests-section">
        <SectionHeader title={t("venner.outgoing_title")} />
        {pending.outgoing.length === 0 ? (
          <p className="font-fraunces italic text-[var(--cream-soft)]">{t("venner.no_outgoing")}</p>
        ) : (
          <ul className="border-y border-white/10 divide-y divide-white/5">
            {pending.outgoing.map((r) => (
              <li key={r.friendship_id} className="py-3 flex items-center justify-between">
                <Link to={`/profile/${r.user?.username}`} className="font-fraunces italic text-lg text-[var(--cream)]">
                  @{r.user?.username}
                </Link>
                <span className="font-meta text-[var(--cream-soft)]">{t("venner.waiting")}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section testId="friends-list-section">
        <SectionHeader icon={Users} title={t("venner.friends_title", { count: friends.length })} />
        {friends.length === 0 ? (
          <p className="font-fraunces italic text-[var(--cream-soft)]">{t("venner.no_friends")}</p>
        ) : (
          <ul className="border-y border-white/10 divide-y divide-white/5" data-testid="friends-list">
            {friends.map((u) => {
              const isClose = closeFriends.find(c => c.username === u.username);
              return (
                <li key={u.id} className="py-3 flex items-center justify-between">
                  <Link to={`/profile/${u.username}`} className="font-fraunces italic text-lg text-[var(--cream)] hover:text-[var(--magenta)]">
                    {isClose && <Star className="h-3.5 w-3.5 inline mr-1.5 text-[var(--gold)]" fill="currentColor" />}
                    @{u.username}
                  </Link>
                  <div className="flex gap-3">
                    <button onClick={()=>toggleCloseFriend(u.username)}
                      className={`font-manrope text-xs uppercase tracking-[0.2em] inline-flex items-center gap-1 ${
                        isClose ? "text-[var(--gold)]" : "text-[var(--cream-soft)] hover:text-[var(--gold)]"
                      }`}
                      data-testid={`toggle-close-${u.username}`}>
                      <Star className="h-3.5 w-3.5" fill={isClose ? "currentColor" : "none"} />
                      {isClose ? t("venner.is_close") : t("venner.make_close")}
                    </button>
                    <button onClick={()=>remove(u.username)}
                      className="font-manrope text-xs uppercase tracking-[0.2em] text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1"
                      data-testid={`remove-friend-${u.username}`}>
                      <UserMinus className="h-3.5 w-3.5" /> {t("venner.remove_friend")}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>

      <Section testId="close-friends-section">
        <SectionHeader icon={Star} title={t("venner.close_title", { count: closeFriends.length })}
          desc={t("venner.close_desc")} />
      </Section>

      <Section testId="friend-groups-section">
        <SectionHeader icon={FolderPlus} title={t("venner.groups_title")}
          desc={t("venner.groups_desc")} />
        <div className="space-y-2 mb-5">
          <input value={newGroupName} onChange={(e)=>setNewGroupName(e.target.value)}
            placeholder={t("venner.group_name_placeholder")} maxLength={40} className="input-editorial w-full"
            data-testid="new-group-name" />
          <textarea value={newGroupMembers} onChange={(e)=>setNewGroupMembers(e.target.value)}
            placeholder={t("venner.group_members_placeholder")}
            rows={2}
            className="input-editorial w-full resize-none"
            data-testid="new-group-members" />
          <button onClick={createGroup}
            className="btn-magenta inline-flex items-center gap-1.5 text-xs"
            data-testid="create-friend-group">
            <FolderPlus className="h-3.5 w-3.5" /> {t("venner.create_group")}
          </button>
        </div>
        {groups.length > 0 && (
          <ul className="border-y border-white/10 divide-y divide-white/5" data-testid="friend-groups-list">
            {groups.map((g) => (
              <li key={g.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-fraunces italic text-lg text-[var(--cream)]">{g.name}</div>
                  <div className="font-meta mt-1 text-[10px]">
                    {(g.members || []).map(m => `@${m.username}`).join(", ") || t("venner.no_members")}
                  </div>
                </div>
                <button onClick={()=>deleteGroup(g.id, g.name)}
                  className="font-manrope text-xs uppercase tracking-[0.2em] text-[var(--cream-soft)] hover:text-[var(--magenta)] inline-flex items-center gap-1"
                  data-testid={`delete-group-${g.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> {t("venner.delete_group")}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}
